import React, { useEffect, useState } from 'react';
import { Bell, ExternalLink, Copy, Check, Info } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { requestNotificationPermission } from '../firebase';
import toast from 'react-hot-toast';

const isNotificationSupported = () =>
  typeof window !== 'undefined' && 'Notification' in window;

export const NotificationPermissionRequest: React.FC = () => {
  const [permission, setPermission] = useState<NotificationPermission>('default');
  const { user } = useAuth();
  const [showPrompt, setShowPrompt] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showDetailedHelp, setShowDetailedHelp] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!isNotificationSupported()) return;
    if (Notification.permission !== 'granted') {
      setShowPrompt(true);
    }
    setPermission(Notification.permission);
  }, []);

  const handleRequestPermission = async () => {
    console.log('Requesting permission...');
    console.log('Permission before request:', Notification.permission);
    
    const permissionBefore = Notification.permission;
    setIsLoading(true);
    
    try {
      const result = await requestNotificationPermission();
      const permissionAfter = Notification.permission;
      
      console.log('Permission after request:', permissionAfter);
      console.log('Permission result:', result);
      
      if (result) {
        console.log('FCM Token received:', result);
        setPermission('granted');
        setShowPrompt(false);
        setShowDetailedHelp(false);
      } else {
        console.log('No FCM token received');
        // Update permission state based on actual browser permission
        console.log('Current permission state:', permissionAfter);
        setPermission(permissionAfter);
        
        // Show detailed help if permission failed or was denied
        if (permissionAfter === 'denied' || (permissionBefore === 'default' && permissionAfter === 'default')) {
          console.log('Permission failed - showing detailed help');
          setShowDetailedHelp(true);
        } else if (permissionAfter === 'granted') {
          setShowPrompt(false);
          setShowDetailedHelp(false);
        }
      }
    } catch (error) {
      console.error('Permission request error:', error);
      setShowDetailedHelp(true);
    } finally {
      setIsLoading(false);
    }
  };

  const getBrowserSettingsUrl = () => {
    const userAgent = navigator.userAgent;
    if (userAgent.indexOf('Edg') > -1) {
      return 'edge://settings/content/notifications';
    } else if (userAgent.indexOf('Chrome') > -1) {
      return 'chrome://settings/content/notifications';
    } else if (userAgent.indexOf('Firefox') > -1) {
      return 'about:preferences#privacy';
    }
    return null;
  };

  const openBrowserSettings = async () => {
    const settingsUrl = getBrowserSettingsUrl();
    
    if (settingsUrl) {
      try {
        await navigator.clipboard.writeText(settingsUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 3000);
        
        toast.success(
          'Settings URL copied! Paste it in your address bar to open notification settings.',
          { duration: 5000 }
        );
        
        // Try to navigate (will likely be blocked)
        try {
          window.location.href = settingsUrl;
        } catch (err) {
          console.log('Direct navigation blocked (expected)');
        }
      } catch (err) {
        console.error('Failed to copy:', err);
        toast.error('Failed to copy URL. Please manually navigate to browser settings.');
      }
    } else {
      toast.error('Please click the lock icon 🔒 in your address bar and enable notifications');
    }
  };

  if (!user || !showPrompt || !isNotificationSupported()) {
    return null;
  }

  const settingsUrl = getBrowserSettingsUrl();
  const isDenied = permission === 'denied';

  // Show simple popup first
  if (!showDetailedHelp) {
    return (
      <div className="fixed bottom-4 right-4 max-w-sm bg-white rounded-lg shadow-lg p-4 animate-fade-in z-50">
        <div className="flex items-center gap-3">
          <Bell className="w-6 h-6 text-yellow-400" />
          <div className="flex-1">
            <h3 className="font-medium">Enable Notifications</h3>
            <p className="text-sm text-gray-600">
              Get notified about your upcoming calls and important updates
            </p>
          </div>
        </div>
        <div className="mt-4 flex gap-2 justify-end">
          <button
            onClick={() => setShowPrompt(false)}
            className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800"
            disabled={isLoading}
          >
            Later
          </button>
          <button
            onClick={handleRequestPermission}
            className="px-3 py-1.5 text-sm bg-yellow-400 text-black rounded-md hover:bg-yellow-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
                Enabling...
              </>
            ) : (
              'Enable'
            )}
          </button>
        </div>
      </div>
    );
  }

  // Show detailed help after clicking Enable and it failed
  return (
    <div className="fixed bottom-4 right-4 max-w-md bg-white rounded-lg shadow-lg p-4 animate-fade-in z-50">
      <div className="flex items-center gap-3">
        <Bell className="w-6 h-6 text-red-500" />
        <div className="flex-1">
          <h3 className="font-medium text-red-900">Notifications are Blocked</h3>
          <p className="text-sm text-gray-600">
            Get notified about your upcoming calls and important updates
          </p>
        </div>
      </div>
      
      <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
        <div className="flex items-start gap-2">
          <Info className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium text-yellow-900 mb-1">
              Quick Fix:
            </p>
            <p className="text-sm text-yellow-800">
              Click the <strong>lock icon 🔒</strong> in your browser's address bar, then change "Notifications" to <strong>"Allow"</strong>
            </p>
          </div>
        </div>
      </div>

      {settingsUrl && (
        <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-md">
          <p className="text-sm font-medium text-blue-900 mb-2">
            Or open browser settings directly:
          </p>
          <div className="flex items-center gap-2 bg-white p-2 rounded border border-blue-200 mb-2">
            <code className="text-xs text-blue-800 flex-1 break-all">{settingsUrl}</code>
            {copied ? (
              <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
            ) : (
              <Copy className="w-4 h-4 text-blue-600 flex-shrink-0" />
            )}
          </div>
          <button
            onClick={openBrowserSettings}
            className="w-full px-3 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center justify-center gap-2"
          >
            <ExternalLink className="w-4 h-4" />
            {copied ? 'URL Copied! Paste in Address Bar' : 'Copy Settings URL'}
          </button>
          <p className="text-xs text-blue-700 mt-2">
            Paste the URL in your address bar to go directly to notification settings
          </p>
        </div>
      )}
      
      <div className="mt-4 flex gap-2 justify-end">
        <button
          onClick={() => {
            setShowPrompt(false);
            setShowDetailedHelp(false);
          }}
          className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800"
        >
          Later
        </button>
        <button
          onClick={() => window.location.reload()}
          className="px-3 py-1.5 text-sm bg-green-600 text-white rounded-md hover:bg-green-700"
        >
          I've Enabled It
        </button>
      </div>
    </div>
  );
}; 